/* iter 241 Lot 4 — Engine context + selector (Multimoteur V2 frontend).
 *
 * Two responsibilities :
 *   1. Fetch the list of engines for a project via GET /api/v2/projects/{id}/engines,
 *      auto-select the primary engine on first mount, persist the active
 *      engine in localStorage per project.
 *   2. Detect if the v2 endpoints are 501 (ENABLE_MULTIMOTEUR_V2 OFF)
 *      and fallback cleanly (multiEnabled=false) so the legacy screens
 *      keep working. */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/lib/api';

const EngineContext = createContext(null);

const storageKey = (projectId) => `ame.activeEngine.${projectId}`;

export function EngineProvider({ projectId, children }) {
  const [engines, setEngines] = useState([]);
  const [activeEngineId, setActiveEngineId] = useState(null);
  const [multiEnabled, setMultiEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/v2/projects/${projectId}/engines`);
      const list = data.engines || [];
      setEngines(list);
      setMultiEnabled(!!data.flag_enabled);
      const stored = window.localStorage.getItem(storageKey(projectId));
      const primary = list.find((e) => e.is_primary);
      const initial = list.find((e) => e.id === stored) || primary || list[0];
      setActiveEngineId(initial ? initial.id : null);
    } catch (e) {
      // 501 or network error → legacy fallback (multi off, no engines).
      if (e?.response?.status === 501) {
        setMultiEnabled(false);
      } else {
        setError(e);
      }
      setEngines([]);
      setActiveEngineId(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchEngine = useCallback((engineId) => {
    setActiveEngineId(engineId);
    if (projectId) {
      window.localStorage.setItem(storageKey(projectId), engineId);
    }
  }, [projectId]);

  const createEngine = useCallback(async (name) => {
    const { data } = await apiClient.post(`/v2/projects/${projectId}/engines`, { name });
    await refresh();
    setActiveEngineId(data.id);
    window.localStorage.setItem(storageKey(projectId), data.id);
    return data;
  }, [projectId, refresh]);

  const value = {
    engines,
    activeEngineId,
    activeEngine: engines.find((e) => e.id === activeEngineId) || null,
    multiEnabled,
    loading,
    error,
    switchEngine,
    createEngine,
    refresh,
  };
  return <EngineContext.Provider value={value}>{children}</EngineContext.Provider>;
}

export function useActiveEngine() {
  const ctx = useContext(EngineContext);
  if (!ctx) {
    // Legacy usage outside provider — degrade to safe defaults.
    return {
      engines: [], activeEngineId: null, activeEngine: null,
      multiEnabled: false, loading: false, error: null,
      switchEngine: () => {}, createEngine: async () => null, refresh: async () => {},
    };
  }
  return ctx;
}

export default EngineContext;
